import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';

export type TabulatorProgressChangedEvent = {
  eventId: number;

  judgeId: number;

  registeredPageantContestantId: number;

  pageantAvailableEventId: number;

  changeType:
    | 'ScoreSaved'
    | 'ScoreDeleted'
    | 'ScoresDeleted'
    | 'JudgeAocChanged'
    | 'ContestantAocChanged';

  updatedAt: string;
};

export type TabulatorJudgeLiveStatus = {
  eventId: number;
  judgeId: number;
  liveStatus: 'Online' | 'Offline' | 'Scoring' | 'LoggedIn';
  isOnline: boolean;
  updatedAt: string;
};

export type TabulatorJudgeScoringContext = {
  eventId: number;
  judgeId: number;

  pageantAvailableEventId?: number | null;
  aocName?: string | null;

  divisionName?: string | null;
  ageGroupName?: string | null;

  registeredContestantId?: number | null;
  contestantName?: string | null;
  contestantNumber?: string | null;

  liveStatus: 'Scoring';

  updatedAt: string;
};

export type TabulatorJudgeContestantAocCompletedEvent = {
  eventId: number;

  judgeId: number;

  judgeLabel?: string;

  registeredPageantContestantId: number;

  contestantNumber?: number | string;

  contestantName: string;

  pageantAvailableEventId: number;

  aocName: string;

  completedAt: string;
};

export type TabulatorContestantAocCompletedEvent = {
  eventId: number;

  registeredPageantContestantId: number;

  contestantNumber?: number | string;

  contestantName: string;

  pageantAvailableEventId: number;

  aocName: string;

  completedAt: string;
};

export type TabulatorAgeGroupAocCompletedEvent = {
  eventId: number;

  pageantAvailableEventId: number;

  aocName: string;

  ageGroupId: number;

  ageGroupName: string;

  completedAt: string;
};

export type TabulatorDivisionAocCompletedEvent = {
  eventId: number;

  pageantAvailableEventId: number;

  aocName: string;

  divisionId: number;

  divisionName: string;

  completedAt: string;
};

export type TabulatorSideAwardsCompletedEvent = {
  eventId: number;

  pageantId: number;

  divisionId: number;

  divisionName: string;

  ageGroupId?: number | null;

  ageGroupName?: string | null;

  usesAgeGroups: boolean;

  completedAt: string;
};

export type TabulatorTestScoreReceivedEvent = {
  eventId: number;

  judgeId: number;

  judgeLabel: string;

  receivedAt: string;
};

export type TabulatorDashboardUpdatedEvent = {
  eventId: number;

  pageantId: number;

  updatedAt: string;
};

type UseLiveTabulatorProgressArgs = {
  eventId: number;

  enabled: boolean;

  onProgressChanged: (
    change:
      TabulatorProgressChangedEvent,
  ) => void;

  onJudgeStatusChanged?: (
    status:
      TabulatorJudgeLiveStatus,
  ) => void;

  onJudgeScoringContextChanged?: (
    context:
      TabulatorJudgeScoringContext,
  ) => void;

  onJudgeContestantAocCompleted?: (
    event:
      TabulatorJudgeContestantAocCompletedEvent,
  ) => void;

  onContestantAocCompleted?: (
    event:
      TabulatorContestantAocCompletedEvent,
  ) => void;

  onTestScoreReceived?: (
    event:
      TabulatorTestScoreReceivedEvent,
  ) => void;

  onAgeGroupAocCompleted?: (
    event:
      TabulatorAgeGroupAocCompletedEvent,
  ) => void;

  onDivisionAocCompleted?: (
    event:
      TabulatorDivisionAocCompletedEvent,
  ) => void;

  onDashboardUpdated?: (
    event:
      TabulatorDashboardUpdatedEvent,
  ) => void;

  onSideAwardsCompleted?: (
    event:
      TabulatorSideAwardsCompletedEvent,
  ) => void;
};


export function useLiveTabulatorProgress({
  eventId,
  enabled,
  onProgressChanged,
  onJudgeStatusChanged,
  onJudgeScoringContextChanged,
  onJudgeContestantAocCompleted,
  onContestantAocCompleted,
  onAgeGroupAocCompleted,
  onDivisionAocCompleted,
  onTestScoreReceived,
  onDashboardUpdated,
  onSideAwardsCompleted,
}: UseLiveTabulatorProgressArgs): void {
  const onProgressChangedRef =
    useRef(onProgressChanged);

    const onJudgeStatusChangedRef =
  useRef(onJudgeStatusChanged);

  const onJudgeScoringContextChangedRef =
  useRef(onJudgeScoringContextChanged);

  const onJudgeContestantAocCompletedRef =
  useRef(
    onJudgeContestantAocCompleted,
  );

  const onDashboardUpdatedRef =
  useRef(onDashboardUpdated);

const onContestantAocCompletedRef =
  useRef(
    onContestantAocCompleted,
  );

  const onTestScoreReceivedRef =
  useRef(onTestScoreReceived);

const onAgeGroupAocCompletedRef =
  useRef(
    onAgeGroupAocCompleted,
  );

const onDivisionAocCompletedRef =
  useRef(
    onDivisionAocCompleted,
  );

  const onSideAwardsCompletedRef =
  useRef(
    onSideAwardsCompleted,
  );

  useEffect(() => {
  onSideAwardsCompletedRef.current =
    onSideAwardsCompleted;
}, [
  onSideAwardsCompleted,
]);

useEffect(() => {
  onAgeGroupAocCompletedRef.current =
    onAgeGroupAocCompleted;
}, [onAgeGroupAocCompleted]);

useEffect(() => {
  onDashboardUpdatedRef.current =
    onDashboardUpdated;
}, [onDashboardUpdated]);

useEffect(() => {
  onDivisionAocCompletedRef.current =
    onDivisionAocCompleted;
}, [onDivisionAocCompleted]);

useEffect(() => {
  onTestScoreReceivedRef.current =
    onTestScoreReceived;
}, [onTestScoreReceived]);

const connectionRef =
  useRef<signalR.HubConnection | null>(null);

const isStartingRef =
  useRef(false);

const retryTimerRef =
  useRef<number | null>(null);

const refreshTimerRef =
  useRef<number | null>(null);

  useEffect(() => {
    onProgressChangedRef.current =
      onProgressChanged;
  }, [onProgressChanged]);

  useEffect(() => {
  onJudgeStatusChangedRef.current =
    onJudgeStatusChanged;
}, [onJudgeStatusChanged]);

useEffect(() => {
  onJudgeContestantAocCompletedRef.current =
    onJudgeContestantAocCompleted;
}, [
  onJudgeContestantAocCompleted,
]);

useEffect(() => {
  onContestantAocCompletedRef.current =
    onContestantAocCompleted;
}, [
  onContestantAocCompleted,
]);

useEffect(() => {
  onJudgeScoringContextChangedRef.current =
    onJudgeScoringContextChanged;
}, [onJudgeScoringContextChanged]);

  useEffect(() => {
    if (!enabled || !eventId) {
      return;
    }

    let isMounted = true;

    const clearRetryTimer = (): void => {
      if (retryTimerRef.current) {
        window.clearTimeout(
          retryTimerRef.current,
        );

        retryTimerRef.current = null;
      }
    };

    const buildConnection =
      (): signalR.HubConnection => {
        const connection =
          new signalR.HubConnectionBuilder()
            .withUrl('/hubs/event-live')
            .withAutomaticReconnect([
              0,
              2000,
              5000,
              10000,
              30000,
            ])
            .configureLogging(
              signalR.LogLevel.Information,
            )
            .build();

      connection.on(
  'TabulatorProgressChanged',
  (
    change:
      TabulatorProgressChangedEvent,
  ) => {
    console.log(
      '[TabulatorProgress] change received',
      change,
    );

    if (refreshTimerRef.current) {
      window.clearTimeout(
        refreshTimerRef.current,
      );
    }

    refreshTimerRef.current =
      window.setTimeout(() => {
        onProgressChangedRef.current(
          change,
        );
      }, 300);
  },
);

connection.on(
  'TabulatorJudgeStatusChanged',
  (status: TabulatorJudgeLiveStatus) => {
    console.log(
      '[TabulatorProgress] judge status changed',
      status,
    );

    onJudgeStatusChangedRef.current?.(
      status,
    );
  },
);

connection.on(
  'TabulatorJudgeScoringContextChanged',
  (context: TabulatorJudgeScoringContext) => {
    console.log(
      '[TabulatorProgress] judge scoring context changed',
      context,
    );

    onJudgeScoringContextChangedRef.current?.(
      context,
    );
  },
);

connection.on(
  'TabulatorDashboardUpdated',
  (
    event:
      TabulatorDashboardUpdatedEvent,
  ) => {
    console.log(
      '📣 TABULATOR DASHBOARD UPDATED',
      event,
    );

    onDashboardUpdatedRef.current?.(
      event,
    );
  },
);

connection.on(
  'TabulatorAgeGroupAocCompleted',
  (
    event:
      TabulatorAgeGroupAocCompletedEvent,
  ) => {
    console.log(
      '[TabulatorProgress] AGE GROUP AOC COMPLETE',
      event,
    );

    onAgeGroupAocCompletedRef.current?.(
      event,
    );
  },
);

connection.on(
  'TabulatorDivisionAocCompleted',
  (
    event:
      TabulatorDivisionAocCompletedEvent,
  ) => {
    console.log(
      '[TabulatorProgress] DIVISION AOC COMPLETE',
      event,
    );

    onDivisionAocCompletedRef.current?.(
      event,
    );
  },
);

connection.on(
  'TabulatorSideAwardsCompleted',
  (
    event:
      TabulatorSideAwardsCompletedEvent,
  ) => {
    console.log(
      '🏆 TABULATOR SIDE AWARDS COMPLETE',
      event,
    );

    onSideAwardsCompletedRef
      .current?.(
        event,
      );
  },
);

connection.on(
  'TabulatorTestScoreReceived',
  (
    event:
      TabulatorTestScoreReceivedEvent,
  ) => {
    console.log(
      '🚨 TABULATOR TEST SCORE RECEIVED',
      event,
    );

    onTestScoreReceivedRef.current?.(
      event,
    );
  },
);

connection.on(
  'TabulatorJudgeContestantAocCompleted',
  (
    event:
      TabulatorJudgeContestantAocCompletedEvent,
  ) => {
    console.log(
      '[TabulatorProgress] judge finished contestant AOC',
      event,
    );

    onJudgeContestantAocCompletedRef
      .current?.(
        event,
      );
  },
);

connection.on(
  'TabulatorContestantAocCompleted',
  (
    event:
      TabulatorContestantAocCompletedEvent,
  ) => {
    console.log(
      '[TabulatorProgress] contestant AOC complete for ALL judges',
      event,
    );

    onContestantAocCompletedRef
      .current?.(
        event,
      );
  },
);

        connection.onreconnecting(
          (error) => {
            console.warn(
              '[TabulatorProgress] reconnecting...',
              error,
            );
          },
        );

        connection.onreconnected(
          async () => {
            console.log(
              '[TabulatorProgress] reconnected',
            );

            try {
              await connection.invoke(
                'JoinReadOnlyTabulatorGroup',
                eventId,
              );

              console.log(
                '[TabulatorProgress] rejoined group',
                `readonly-tabulator-${eventId}`,
              );
            } catch (error) {
              console.warn(
                '[TabulatorProgress] failed to rejoin group',
                error,
              );
            }
          },
        );

        connection.onclose((error) => {
          console.warn(
            '[TabulatorProgress] closed',
            error,
          );
        });

        return connection;
      };

    const startConnection =
      async (): Promise<void> => {
        if (!isMounted) {
          return;
        }

        if (!navigator.onLine) {
          return;
        }

        if (isStartingRef.current) {
          return;
        }

        let connection =
          connectionRef.current;

        if (
          connection &&
          (
            connection.state ===
              signalR.HubConnectionState
                .Connected ||
            connection.state ===
              signalR.HubConnectionState
                .Connecting ||
            connection.state ===
              signalR.HubConnectionState
                .Reconnecting
          )
        ) {
          return;
        }

        isStartingRef.current = true;

        try {
          if (!connection) {
            connection =
              buildConnection();

            connectionRef.current =
              connection;
          }

          await connection.start();

          if (!isMounted) {
            return;
          }

          console.log(
            '[TabulatorProgress] connected',
            connection.connectionId,
          );

          await connection.invoke(
            'JoinReadOnlyTabulatorGroup',
            eventId,
          );

          console.log(
            '[TabulatorProgress] joined group',
            `readonly-tabulator-${eventId}`,
          );
        } catch (error) {
          console.warn(
            '[TabulatorProgress] connection failed',
            error,
          );

          clearRetryTimer();

          retryTimerRef.current =
            window.setTimeout(() => {
              void startConnection();
            }, 3000);
        } finally {
          isStartingRef.current = false;
        }
      };

    const forceRestartConnection =
      async (): Promise<void> => {
        if (!navigator.onLine) {
          return;
        }

        const existingConnection =
          connectionRef.current;

  

        try {
          if (existingConnection) {
            existingConnection.off(
              'TabulatorProgressChanged',
            );
            existingConnection.off(
              'TabulatorJudgeStatusChanged',
            );
            existingConnection.off(
              'TabulatorJudgeScoringContextChanged',
            );

            existingConnection.off(
  'TabulatorSideAwardsCompleted',
);

            existingConnection.off(
  'TabulatorJudgeContestantAocCompleted',
);

existingConnection.off(
  'TabulatorDashboardUpdated',
);

existingConnection.off(
  'TabulatorContestantAocCompleted',
);

existingConnection.off(
  'TabulatorTestScoreReceived',
);



            await existingConnection
              .stop()
              .catch(() => undefined);
          }
        } finally {
          connectionRef.current = null;

          void startConnection();
        }
      };

    const handleOnline = (): void => {
      console.log(
        '[TabulatorProgress] browser online — reconnecting',
      );

      void forceRestartConnection();
    };

    const handleFocusOrVisible =
      (): void => {
        if (
          document.visibilityState ===
          'visible'
        ) {
          void startConnection();
        }
      };

    window.addEventListener(
      'online',
      handleOnline,
    );

    window.addEventListener(
      'focus',
      handleFocusOrVisible,
    );

    document.addEventListener(
      'visibilitychange',
      handleFocusOrVisible,
    );

    void startConnection();

  return () => {
  isMounted = false;

  clearRetryTimer();

  if (refreshTimerRef.current) {
    window.clearTimeout(
      refreshTimerRef.current,
    );

    refreshTimerRef.current = null;
  }

  window.removeEventListener(
    'online',
    handleOnline,
  );

  window.removeEventListener(
    'focus',
    handleFocusOrVisible,
  );

  document.removeEventListener(
    'visibilitychange',
    handleFocusOrVisible,
  );

  const connection =
    connectionRef.current;

  connectionRef.current = null;

if (connection) {
  connection.off(
    'TabulatorProgressChanged',
  );

  connection.off(
    'TabulatorJudgeStatusChanged',
  );

  connection.off(
  'TabulatorAgeGroupAocCompleted',
);

connection.off(
  'TabulatorDashboardUpdated',
);

connection.off(
  'TabulatorSideAwardsCompleted',
);

connection.off(
  'TabulatorTestScoreReceived',
);

connection.off(
  'TabulatorDivisionAocCompleted',
);

  void connection
    .invoke(
      'LeaveReadOnlyTabulatorGroup',
      eventId,
    )
    .catch(() => undefined)
    .finally(() => {
      void connection.stop();
    });
}
};
  }, [eventId, enabled]);
}